import type { RemoteCollectionSorting } from "../data/remote-collection"
import {
  defineGraphQLConnectionQuery,
  graphQLConnectionVariables,
  type GraphQLConnection,
  type GraphQLConnectionQueryConsistency,
  type GraphQLConnectionQueryDefinition,
  type GraphQLConnectionVariables,
  type ConnectionPagingVariablesFor,
} from "./graphql-connection-query"
import {
  createGraphQLSelection,
  isGraphQLSelection,
  type AnyGraphQLDocument,
  type GraphQLDocumentData,
  type GraphQLSelection,
  type NoGraphQLVariables,
  type GraphQLResultMapping,
} from "./graphql-document"
import { defineGraphQLQuery, type GraphQLQueryDefinition, type GraphQLQueryCodec } from "./graphql-query"
import type { GraphQLEnumDescriptor } from "./graphql-enum"
import type { GraphQLInputDescriptor, GraphQLInputField, GraphQLInputShape } from "./graphql-input"

const variableMarker = Symbol("florisoft.graphql-variable")
const argsMarker = Symbol("florisoft.graphql-args")
const pagedMarker = Symbol("florisoft.graphql-paged")
const aliasMarker = Symbol("florisoft.graphql-alias")
declare const argsFieldBrand: unique symbol
declare const pagedFieldBrand: unique symbol
declare const selectionTypeBrand: unique symbol

// Schema markers, written by the generated schema module. They exist only in types.

/** A schema field that takes arguments: its result type and the arguments it accepts. */
export interface GraphQLArgsField<TResult, TArgs> {
  readonly [argsFieldBrand]: { readonly result: TResult; readonly args: TArgs }
}

/** A schema field with `[UsePaging]`: its node type, its own arguments and the node fields it can be sorted by. */
export interface GraphQLPagedField<TNode, TArgs, TSortField extends string = never> {
  readonly [pagedFieldBrand]: { readonly node: TNode; readonly args: TArgs; readonly sort: TSortField }
}

// Selection markers, written by views.

/** `args(values, selection)`: select a field that takes arguments. */
export interface GraphQLArgsSelection<TValues, TSelection> {
  readonly [argsMarker]: { readonly values: TValues; readonly selection: TSelection }
}

/** `paged(values, nodeSelection)`: select the nodes of a paged field. */
export interface GraphQLPagedSelection<TValues, TSelection> {
  readonly [pagedMarker]: { readonly values: TValues; readonly selection: TSelection }
}

/** `paged(nodeSelection)`: select the nodes of a paged field; its own arguments become query parameters. */
export interface GraphQLInferredPagedSelection<TSelection> {
  readonly [pagedMarker]: { readonly values: undefined; readonly selection: TSelection }
}

/** `alias(field, selection)`: select `field` under the key it is written at. */
export interface GraphQLAliasSelection<TField extends string, TSelection> {
  readonly [aliasMarker]: { readonly field: TField; readonly selection: TSelection }
}

export function args<const TValues extends object, const TSelection>(
  values: TValues,
  selection: TSelection
): GraphQLArgsSelection<TValues, TSelection> {
  return Object.freeze({ [argsMarker]: Object.freeze({ values, selection }) })
}

/** Selects the nodes of a paged field and takes the field's own arguments as parameters of the query. */
export function paged<const TSelection>(nodeSelection: TSelection): GraphQLInferredPagedSelection<TSelection>
/** Selects the nodes of a paged field with literal values or parameters (`$`) for its arguments. */
export function paged<const TValues extends object, const TSelection>(
  values: TValues,
  nodeSelection: TSelection
): GraphQLPagedSelection<TValues, TSelection>
export function paged(...parameters: [selection: unknown] | [values: object, selection: unknown]) {
  return Object.freeze({
    [pagedMarker]: Object.freeze(
      parameters.length === 1
        ? { values: undefined, selection: parameters[0] }
        : { values: parameters[0], selection: parameters[1] }
    ),
  })
}

/**
 * Selects `field` under the key that the alias is written at, as `key: field` in the document. The selection is
 * whatever the field itself would take: `true`, a nested selection, `args(...)`, `paged(...)` or a plain selection
 * that infers the arguments of the field.
 */
export function alias<const TField extends string, const TSelection>(
  field: TField,
  selection: TSelection
): GraphQLAliasSelection<TField, TSelection> {
  return Object.freeze({ [aliasMarker]: Object.freeze({ field, selection }) })
}

// Selection types: what a view may write, and what comes back.

type Leaf<TValue> = NonNullable<TValue> extends ReadonlyArray<infer TItem> ? NonNullable<TItem> : NonNullable<TValue>

type ValueSelection<TValue> = [Leaf<TValue>] extends [object]
  ? GraphQLObjectSelection<Leaf<TValue>> | GraphQLSelection<unknown>
  : true

type PagedValues<TArgs, TSortField extends string> = TArgs & {
  readonly first?: number
  readonly after?: string | null
  readonly before?: string | null
  readonly last?: number
  readonly order?: ReadonlyArray<{ readonly [TField in TSortField]?: "ASC" | "DESC" }>
}

// A plain selection is not an args(...) or paged(...) marker, so the argument values of those are always checked.
type Unmarked<TSelection> = TSelection extends object
  ? TSelection & { readonly [argsMarker]?: never; readonly [pagedMarker]?: never }
  : TSelection

// A field with arguments is selected with args(...), or plainly: then its arguments become parameters of the query.
type FieldSelection<TField> = [TField] extends [GraphQLArgsField<infer TResult, infer TArgs>]
  ? GraphQLArgsSelection<TArgs, ValueSelection<TResult>> | Unmarked<ValueSelection<TResult>>
  : [TField] extends [GraphQLPagedField<infer TNode, infer TArgs, infer TSortField>]
    ? | GraphQLPagedSelection<PagedValues<TArgs, TSortField>, ValueSelection<TNode>>
      | GraphQLInferredPagedSelection<ValueSelection<TNode>>
    : ValueSelection<TField>

/**
 * The fields of one schema type that a selection may contain. Any key may also hold an `alias(field, selection)` of
 * one of the fields; other keys are aliases only and are not listed here.
 */
export type GraphQLObjectSelection<TType> = {
  readonly [TKey in keyof TType]?: FieldSelection<TType[TKey]> | GraphQLAliasSelection<keyof TType & string, unknown>
}

// The marker is found by its brand, so a schema type may have a field named `selection` or `values`.
type InnerSelection<TSelection> = TSelection extends
  | { readonly [argsMarker]: { readonly selection: infer TInner } }
  | { readonly [pagedMarker]: { readonly selection: infer TInner } }
  ? TInner
  : TSelection

type Shaped<TValue, TSelection> =
  TSelection extends GraphQLSelection<infer TData>
    ? TData
    : [TSelection] extends [true]
      ? TValue
      : TValue extends ReadonlyArray<infer TItem>
        ? ReadonlyArray<Shaped<TItem, TSelection>>
        : TValue extends null
          ? null
          : GraphQLObjectResult<TValue, TSelection>

type FieldResult<TField, TSelection> = [TField] extends [GraphQLArgsField<infer TResult, unknown>]
  ? Shaped<TResult, InnerSelection<TSelection>>
  : [TField] extends [GraphQLPagedField<infer TNode, unknown, string>]
    ? GraphQLConnection<Shaped<TNode, InnerSelection<TSelection>>>
    : Shaped<Exclude<TField, undefined>, TSelection>

// The schema field that a selection key stands for: the aliased field of an alias, or the key itself.
type FieldKey<TType, TKey, TEntry> =
  TEntry extends GraphQLAliasSelection<infer TField, unknown>
    ? Extract<TField, keyof TType>
    : Extract<TKey, keyof TType>

type FieldEntry<TEntry> = TEntry extends GraphQLAliasSelection<string, infer TInner> ? TInner : TEntry

/** The data that a selection returns from one schema type. Alias keys hold the result of the aliased field. */
export type GraphQLObjectResult<TType, TSelection> = {
  readonly [
    TKey in keyof TSelection as [FieldKey<TType, TKey, TSelection[TKey]>] extends [never] ? never : TKey
  ]-?: FieldResult<TType[FieldKey<TType, TKey, TSelection[TKey]>], FieldEntry<TSelection[TKey]>>
}

// Generic inference drops excess property checks, so the selection is also intersected with a copy of itself that
// turns every unknown field and argument into `never`. Argument values are checked inside as well: input objects and
// `where` filters, so an unknown input field or filter operation is an error.
type ValidKeys<TAllowed, TValues> = {
  readonly [TKey in keyof TValues]: TKey extends keyof TAllowed ? ValidArgument<TAllowed[TKey], TValues[TKey]> : never
}

type ValidArgument<TAllowed, TValue> = unknown extends TAllowed
  ? TValue // the opaque scalar Any takes any value
  : TValue extends ReadonlyArray<unknown>
    ? { readonly [TIndex in keyof TValue]: ValidArgument<ItemOf<TAllowed>, TValue[TIndex]> }
    : TValue extends object
      ? NonNullable<TAllowed> extends infer TObject extends object
        ? ValidKeys<TObject, TValue>
        : TValue
      : TValue

type ItemOf<TValue> = NonNullable<TValue> extends ReadonlyArray<infer TItem> ? TItem : never

type ValidValue<TValue, TSelection> =
  TSelection extends GraphQLSelection<unknown>
    ? TSelection
    : [Leaf<TValue>] extends [object]
      ? [TSelection] extends [true]
        ? never
        : ValidSelection<Leaf<TValue>, TSelection>
      : [TSelection] extends [true]
        ? true
        : never

type ValidField<TField, TSelection> = [TField] extends [GraphQLArgsField<infer TResult, infer TArgs>]
  ? [TSelection] extends [GraphQLArgsSelection<infer TValues, infer TInner>]
    ? GraphQLArgsSelection<ValidKeys<TArgs, TValues>, ValidValue<TResult, TInner>>
    : ValidValue<TResult, TSelection>
  : [TField] extends [GraphQLPagedField<infer TNode, infer TArgs, infer TSortField>]
    ? [TSelection] extends [GraphQLInferredPagedSelection<infer TInner>]
      ? GraphQLInferredPagedSelection<ValidValue<TNode, TInner>>
      : [TSelection] extends [GraphQLPagedSelection<infer TValues, infer TInner>]
        ? GraphQLPagedSelection<ValidKeys<PagedValues<TArgs, TSortField>, TValues>, ValidValue<TNode, TInner>>
        : never
    : ValidValue<TField, TSelection>

// An alias is checked first: its key may equal the name of another field. Its selection is checked against the
// aliased field, and an alias of a field that the type does not have is an error.
type ValidSelection<TType, TSelection> = {
  readonly [TKey in keyof TSelection]: TSelection[TKey] extends GraphQLAliasSelection<infer TField, infer TInner>
    ? [TField] extends [keyof TType]
      ? GraphQLAliasSelection<TField, ValidField<TType[TField], TInner> & FieldSelection<TType[TField]>>
      : never
    : TKey extends keyof TType
      ? ValidField<TType[TKey], TSelection[TKey]>
      : TKey extends typeof selectionTypeBrand
        ? TSelection[TKey]
        : never
}

/** A reusable selection of one schema type, created by `schema.select`. */
export type GraphQLSelectedSelection<TType, TSelection> = TSelection & { readonly [selectionTypeBrand]?: TType }

/** The row type of a `schema.select(...)` selection. */
export type Selected<TSelection> =
  TSelection extends GraphQLSelection<infer TData>
    ? NonNullable<TData>
    : TSelection extends { readonly [selectionTypeBrand]?: infer TType }
      ? GraphQLObjectResult<NonNullable<TType>, TSelection>
      : never

/** The data type of a query or connection query defined with a schema. */
export type QueryData<TQuery extends { readonly document: AnyGraphQLDocument }> = NonNullable<
  GraphQLDocumentData<TQuery["document"]>
>

type QueryParameters<TParameters extends object> = [keyof TParameters] extends [never] ? void : TParameters

type UnionToIntersection<TUnion> = (TUnion extends unknown ? (value: TUnion) => void : never) extends (
  value: infer TIntersection
) => void
  ? TIntersection
  : never

// The arguments of every field selected without args(...), found by walking the selection next to its schema type.
// An explicit args(...) or paged(values, ...) contributes nothing at its own level; its nested selection is still walked.
type ArgumentsOf<TType, TSelection> = {
  [TKey in keyof TSelection]-?: [FieldKey<TType, TKey, TSelection[TKey]>] extends [never]
    ? never
    : FieldArguments<TType[FieldKey<TType, TKey, TSelection[TKey]>], FieldEntry<TSelection[TKey]>>
}[keyof TSelection]

type NestedArguments<TValue, TSelection> =
  TSelection extends GraphQLSelection<unknown>
    ? never
    : [Leaf<TValue>] extends [object]
      ? ArgumentsOf<Leaf<TValue>, TSelection>
      : never

type FieldArguments<TField, TSelection> = [TField] extends [GraphQLArgsField<infer TResult, infer TArgs>]
  ? TSelection extends GraphQLArgsSelection<unknown, infer TInner>
    ? NestedArguments<TResult, TInner>
    : TArgs | NestedArguments<TResult, TSelection>
  : [TField] extends [GraphQLPagedField<infer TNode, infer TArgs, string>]
    ? TSelection extends GraphQLInferredPagedSelection<infer TInner>
      ? TArgs | NestedArguments<TNode, TInner>
      : TSelection extends GraphQLPagedSelection<unknown, infer TInner>
        ? NestedArguments<TNode, TInner>
        : never
    : NestedArguments<TField, TSelection>

/** The arguments that a selection takes over from its fields: `unknown` when every field has explicit arguments. */
export type InferredArguments<TType, TSelection> = UnionToIntersection<ArgumentsOf<TType, TSelection>>

type Flatten<TValue> = { [TKey in keyof TValue]: TValue[TKey] }

/** The explicit `$` parameters together with the arguments inferred from the selection. */
type QueryInputs<TParameters extends object, TType, TSelection> = [unknown] extends [
  InferredArguments<TType, TSelection>,
]
  ? TParameters
  : Flatten<TParameters & InferredArguments<TType, TSelection>>

// The key under which the data of a paged field arrives: the field's own name, or the key of an alias of it.
type PagedKey<TSelection> = {
  [TKey in keyof TSelection]-?: FieldEntry<TSelection[TKey]> extends { readonly [pagedMarker]: object } ? TKey : never
}[keyof TSelection]

type PagedSortField<TQuery, TSelection> =
  PagedKey<TSelection> extends infer TKey
    ? TKey extends keyof TSelection
      ? FieldKey<TQuery, TKey, TSelection[TKey]> extends infer TField
        ? TField extends keyof TQuery
          ? TQuery[TField] extends GraphQLPagedField<unknown, unknown, infer TSortField>
            ? TSortField
            : never
          : never
        : never
      : never
    : never

type PagedNode<TData, TSelection> =
  PagedKey<TSelection> extends infer TKey
    ? TKey extends keyof TData
      ? TData[TKey] extends GraphQLConnection<infer TNode>
        ? TNode
        : never
      : never
    : never

/** The variables of a connection query: the parameters, the paging variables, and `order` when the field can be sorted. */
type ConnectionVariables<TParameters, TSortField extends string, TBackendField extends string> = TParameters &
  GraphQLConnectionVariables<TBackendField> &
  ([TSortField] extends [never] ? unknown : { readonly order: Partial<Record<TBackendField, "ASC" | "DESC">>[] })

export interface GraphQLConnectionQueryOptions<
  TParameters,
  TSortField extends string,
  TSorting extends Readonly<Record<string, TSortField>>,
  TNode,
> {
  /** Sort ids of the UI, mapped to the sortable fields of the node. */
  sorting?: TSorting
  defaultSorting?: RemoteCollectionSorting<NoInfer<Extract<keyof TSorting, string>>>
  maxSortFields?: number
  usePageInfoFlags?: boolean
  consistency?: GraphQLConnectionQueryConsistency<TParameters, TNode>
}

// Runtime facts the builder needs from the generated schema.

export interface GraphQLFieldDescriptor {
  /** GraphQL type name of an object field, or of the nodes of a paged field. */
  readonly type?: () => GraphQLTypeDescriptor
  readonly enum?: GraphQLEnumDescriptor
  /** GraphQL type of every argument, such as `Int!` or `OrderItemId!`. */
  readonly args?: Readonly<Record<string, GraphQLArgumentDescriptor>>
  /** The field has `[UsePaging]`. */
  readonly paged?: true
  /** The name of the sort input of a paged field with `[UseSorting]`, such as `StockItemSortInput`. */
  readonly sorted?: string
  /** The `where` input of a field with `[UseFiltering]`. */
  readonly filter?: () => GraphQLInputDescriptor
}

/** Endpoint-neutral input facts. Value objects resolve through the endpoint's scalar bindings. */
export type GraphQLArgumentDescriptor = (
  | { readonly builtin: string }
  | { readonly scalar: string }
  | { readonly enum: GraphQLEnumDescriptor }
  | { readonly input: () => GraphQLInputDescriptor }
  | { readonly list: GraphQLArgumentDescriptor }
) & { readonly nullable: boolean }

/** One namespace-owned GraphQL object. Nested descriptors resolve only when selected. */
export interface GraphQLTypeDescriptor<TType = unknown> {
  readonly name: string
  readonly fields: Readonly<Record<string, GraphQLFieldDescriptor>>
  readonly [selectionTypeBrand]?: TType
}

/** The GraphQL types of one endpoint by name. `Query` holds the endpoint's query fields. */
export interface GraphQLSchemaTypes {
  readonly Query: object
}

export interface GraphQLSchema<TTypes extends GraphQLSchemaTypes> {
  readonly endpoint: string
  readonly scalars: Readonly<Record<string, string>>
  /**
   * Builds the document of a query from `($) => selection`, where `$` holds explicit parameters named like their
   * variables. Fields with arguments selected without `args(...)` add their arguments as parameters.
   */
  query<TParameters extends object = NoGraphQLVariables, const TSelection = never>(
    name: string,
    build: (
      $: TParameters
    ) => TSelection & GraphQLObjectSelection<TTypes["Query"]> & ValidSelection<TTypes["Query"], TSelection>
  ): GraphQLQueryDefinition<
    GraphQLObjectResult<TTypes["Query"], TSelection>,
    QueryParameters<QueryInputs<TParameters, TTypes["Query"], TSelection>>,
    QueryInputs<TParameters, TTypes["Query"], TSelection>
  >

  /**
   * Builds a paged query with the Florisoft paging variables from one `paged(...)` field. A field selected with
   * `paged(nodeSelection)` adds its own arguments as parameters.
   */
  connectionQuery<
    TParameters extends object = NoGraphQLVariables,
    const TSelection = never,
    const TSorting extends Readonly<Record<string, PagedSortField<TTypes["Query"], TSelection>>> = Readonly<
      Record<never, never>
    >,
  >(
    name: string,
    build: (
      $: TParameters
    ) => TSelection & GraphQLObjectSelection<TTypes["Query"]> & ValidSelection<TTypes["Query"], TSelection>,
    options?: GraphQLConnectionQueryOptions<
      QueryParameters<QueryInputs<TParameters, TTypes["Query"], TSelection>>,
      PagedSortField<TTypes["Query"], TSelection>,
      TSorting,
      PagedNode<GraphQLObjectResult<TTypes["Query"], TSelection>, TSelection>
    >
  ): GraphQLConnectionQueryDefinition<
    GraphQLObjectResult<TTypes["Query"], TSelection>,
    PagedNode<GraphQLObjectResult<TTypes["Query"], TSelection>, TSelection>,
    QueryParameters<QueryInputs<TParameters, TTypes["Query"], TSelection>>,
    ConnectionVariables<
      QueryInputs<TParameters, TTypes["Query"], TSelection>,
      PagedSortField<TTypes["Query"], TSelection>,
      TSorting[keyof TSorting]
    >,
    Extract<keyof TSorting, string>,
    TSorting[keyof TSorting]
  >

  /** A reusable selection of one type, used as `args(values, selection)` or as a nested selection. */
  select<const TName extends keyof TTypes & string, const TSelection>(
    name: TName,
    selection: TSelection & GraphQLObjectSelection<TTypes[TName]> & ValidSelection<TTypes[TName], TSelection>
  ): GraphQLSelectedSelection<TTypes[TName], TSelection>

  /** The selection of one type as a framework selection block, such as the `RootUseCase` fields of the Root query. */
  selection<TType, const TSelection>(
    type: GraphQLTypeDescriptor<TType>,
    selection: TSelection & GraphQLObjectSelection<TType> & ValidSelection<TType, TSelection>
  ): GraphQLSelection<GraphQLObjectResult<TType, TSelection>>
}

// Document builder.

const identifier = /^[_A-Za-z][_0-9A-Za-z]*$/
const pagingArguments = { first: "Int", after: "String", before: "String", last: "Int" } as const
const connectionVariables = {
  first: "Int!",
  after: "String",
  before: "String",
  skip: "Int!",
  includeTotalCount: "Boolean!",
} as const

type Selection = Readonly<Record<string, unknown>>

interface MarkerContent {
  readonly values: Selection | undefined
  readonly selection: unknown
}

interface AliasContent {
  readonly field: string
  readonly selection: unknown
}

/** What alias(...) wrapped, or undefined when the value is another kind of selection. */
function aliasContent(value: unknown): AliasContent | undefined {
  return typeof value === "object" && value !== null ? (value as Record<symbol, AliasContent>)[aliasMarker] : undefined
}

/** What args(...) or paged(...) wrapped, or undefined when the value is another kind of selection. */
function markerContent(value: unknown, marker: symbol): MarkerContent | undefined {
  return typeof value === "object" && value !== null ? (value as Record<symbol, MarkerContent>)[marker] : undefined
}

function isVariable(value: unknown): value is { readonly [variableMarker]: string } {
  return typeof value === "object" && value !== null && variableMarker in value
}

function isObject(value: unknown): value is Selection {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

class DocumentBuilder {
  readonly variables = new Map<string, string>()
  private readonly enums = new Map<string, GraphQLEnumDescriptor>()
  private readonly inputTypes = new Map<string, GraphQLInputDescriptor>()
  private readonly sortInputs = new Set<string>()
  private readonly inputs = new Map<string, string>()
  /** `variables` is false for selection blocks: they have no operation to declare variables on. */
  constructor(
    private readonly scalars: Readonly<Record<string, string>>,
    private readonly variablesAllowed = true
  ) {}

  fields(typeName: GraphQLTypeDescriptor, selection: unknown, path: string, connection = false): string {
    if (!isObject(selection) || Object.keys(selection).length === 0) {
      throw new Error(`GraphQL selection ${path} needs at least one field.`)
    }
    return Object.entries(selection)
      .map(([key, value]) => this.field(typeName, key, value, `${path}.${key}`, connection))
      .join(" ")
  }

  private field(
    typeName: GraphQLTypeDescriptor,
    key: string,
    value: unknown,
    path: string,
    connection: boolean
  ): string {
    const aliased = aliasContent(value)
    if (aliased === undefined) return this.selectedField(typeName, key, value, path, connection)
    if (!identifier.test(key)) throw new Error(`GraphQL alias ${path}: "${key}" is not a valid GraphQL name.`)
    if (!identifier.test(aliased.field)) {
      throw new Error(`GraphQL alias ${path}: "${aliased.field}" is not a valid GraphQL field name.`)
    }
    if (aliasContent(aliased.selection) !== undefined) throw new Error(`GraphQL alias ${path} cannot alias an alias.`)
    return `${key}: ${this.selectedField(typeName, aliased.field, aliased.selection, path, connection)}`
  }

  /** One field as the document writes it: its arguments, its nested selection and its paged structure. */
  private selectedField(
    typeName: GraphQLTypeDescriptor,
    key: string,
    value: unknown,
    path: string,
    connection: boolean
  ): string {
    const field = typeName.fields[key] ?? {}
    const nestedType = field.type?.()
    const pagedSelection = markerContent(value, pagedMarker)
    if (pagedSelection !== undefined) {
      if (!field.paged || nestedType === undefined) throw new Error(`GraphQL field ${path} is not a paged field.`)
      return this.pagedField(key, nestedType, field, pagedSelection, path, connection)
    }
    if (field.paged) throw new Error(`GraphQL field ${path} is paged: select it with paged(...).`)
    const argsSelection = markerContent(value, argsMarker)
    if (argsSelection !== undefined) {
      if (!takesArguments(field)) throw new Error(`GraphQL field ${path} takes no arguments.`)
      return `${key}${this.argumentsText(this.argumentTypes(field), argsSelection.values, path)}${this.nested(nestedType, argsSelection.selection, path)}`
    }
    if (takesArguments(field)) {
      // A field with arguments, selected without args(...): every argument becomes a variable of the same name.
      const types = this.argumentTypes(field)
      return `${key}${this.argumentsText(types, this.inferredValues(types, path), path)}${this.nested(nestedType, value, path)}`
    }
    return key + this.nested(nestedType, value, path)
  }

  /** The variable of every argument, named like the argument. */
  private inferredValues(types: Readonly<Record<string, string>>, path: string): Selection {
    if (!this.variablesAllowed) {
      throw new Error(
        `GraphQL field ${path} takes arguments: a selection block has no variables, select it with args(...).`
      )
    }
    return Object.fromEntries(Object.keys(types).map((name) => [name, { [variableMarker]: name }]))
  }

  private nested(typeName: GraphQLTypeDescriptor | undefined, selection: unknown, path: string) {
    if (isGraphQLSelection(selection)) return ` { ${selection.source} }`
    if (typeName === undefined) {
      if (selection !== true) throw new Error(`GraphQL field ${path} is a scalar or enum: select it with true.`)
      return ""
    }
    return ` { ${this.fields(typeName, selection, path)} }`
  }

  private pagedField(
    key: string,
    nodeType: GraphQLTypeDescriptor,
    field: GraphQLFieldDescriptor,
    marker: MarkerContent,
    path: string,
    connection: boolean
  ) {
    const sortInput = field.sorted === undefined ? undefined : `[${field.sorted}!]`
    if (field.sorted !== undefined) this.sortInputs.add(field.sorted)
    const ownTypes = this.argumentTypes(field)
    const types: Record<string, string> = {
      ...ownTypes,
      ...pagingArguments,
      ...(sortInput === undefined ? {} : { order: sortInput }),
    }
    // paged(nodeSelection) has no values: the field's own arguments are inferred, the paging arguments never are.
    const values = marker.values ?? this.inferredValues(ownTypes, path)
    let argumentList: string
    let result: string
    if (connection) {
      for (const name of Object.keys(pagingArguments).concat("order")) {
        if (name in values) throw new Error(`GraphQL field ${path} gets ${name} from connectionQuery.`)
      }
      const own = this.argumentList(types, values, path)
      const paging = ["first: $first", "after: $after", "before: $before", "last: $skip"]
      if (sortInput !== undefined) paging.push("order: $order")
      argumentList = [own, ...paging].filter(Boolean).join(", ")
      for (const [name, type] of Object.entries(connectionVariables)) this.declare(name, type, path)
      if (sortInput !== undefined) this.declare("order", sortInput, path)
      result = "totalCount @include(if: $includeTotalCount) pageInfo { startCursor endCursor } "
    } else {
      argumentList = this.argumentList(types, values, path)
      result = ""
    }
    const nodes = this.fields(nodeType, marker.selection, path)
    return `${key}${argumentList ? `(${argumentList})` : ""} { ${result}nodes { ${nodes} } }`
  }

  /** The parenthesized arguments, or nothing when none are written. */
  private argumentsText(types: Readonly<Record<string, string>>, values: Selection | undefined, path: string) {
    const list = this.argumentList(types, values ?? {}, path)
    return list === "" ? "" : `(${list})`
  }

  private argumentList(types: Readonly<Record<string, string>>, values: Selection, path: string) {
    return Object.entries(values)
      .filter(([, value]) => value !== undefined)
      .map(([name, value]) => {
        const type = types[name]
        if (type === undefined) throw new Error(`GraphQL argument ${path}(${name}): it is not an argument.`)
        return `${name}: ${this.literal(value, type, `${path}(${name})`)}`
      })
      .join(", ")
  }

  private literal(value: unknown, type: string, path: string): string {
    if (isVariable(value)) {
      if (!this.variablesAllowed) {
        throw new Error(`GraphQL argument ${path}: a selection block has no variables, write a literal value.`)
      }
      const name = value[variableMarker]
      this.declare(name, type, path)
      this.inputs.set(name, type)
      return `$${name}`
    }
    if (value === null) return "null"
    const named = type.endsWith("!") ? type.slice(0, -1) : type
    if (named.startsWith("[")) {
      const item = named.slice(1, -1)
      return Array.isArray(value)
        ? `[${value.map((entry, index) => this.literal(entry, item, `${path}[${index}]`)).join(", ")}]`
        : this.literal(value, item, path)
    }
    if (this.enums.has(named)) return enumLiteral(this.enums.get(named)!.toGraphQL(value as number), path)
    const input = this.inputTypes.get(named)
    if (input !== undefined) return this.inputObject(value, input, path)
    // The opaque scalar Any takes any JSON value, written as a GraphQL literal.
    if (named === "Any") return this.inputValue(value, undefined, path)
    if (typeof value === "string") return JSON.stringify(value)
    if (typeof value === "boolean") return String(value)
    if (typeof value === "number" && Number.isFinite(value)) return String(value)
    if (this.sortInputs.has(named) && isObject(value)) {
      const entries = Object.entries(value).map(([field, direction]) => `${field}: ${enumLiteral(direction, path)}`)
      return `{ ${entries.join(", ")} }`
    }
    throw new Error(`GraphQL argument ${path}: ${JSON.stringify(value)} is not supported as a ${named} value.`)
  }

  /** An input object as a GraphQL object literal: enums are names, nested objects and lists are written recursively. */
  private inputObject(value: unknown, shape: GraphQLInputShape, path: string): string {
    if (!isObject(value)) throw new Error(`GraphQL argument ${path}: ${JSON.stringify(value)} is not an object.`)
    const entries = Object.entries(value)
      .filter(([, entry]) => entry !== undefined)
      .map(([key, entry]) => {
        if (!identifier.test(key)) throw new Error(`GraphQL argument ${path}: "${key}" is not a field name.`)
        return `${key}: ${this.inputValue(entry, shape.fields?.[key], `${path}.${key}`)}`
      })
    return `{ ${entries.join(", ")} }`
  }

  private inputValue(value: unknown, field: GraphQLInputField | undefined, path: string): string {
    if (value === null) return "null"
    if (Array.isArray(value)) {
      return `[${value.map((item, index) => this.inputValue(item, field, `${path}[${index}]`)).join(", ")}]`
    }
    if (field !== undefined) {
      return "enum" in field
        ? enumLiteral(field.enum.toGraphQL(value as number), path)
        : this.inputObject(value, field.input(), path)
    }
    if (typeof value === "string") return JSON.stringify(value)
    if (typeof value === "boolean") return String(value)
    if (typeof value === "number" && Number.isFinite(value)) return String(value)
    if (isObject(value)) return this.inputObject(value, {}, path)
    throw new Error(`GraphQL argument ${path}: ${JSON.stringify(value)} is not supported as an input value.`)
  }

  private declare(name: string, type: string, path: string) {
    const existing = this.variables.get(name)
    if (existing !== undefined && existing !== type) {
      throw new Error(`GraphQL variable $${name} is used as ${existing} and as ${type} (${path}).`)
    }
    this.variables.set(name, type)
  }

  private argumentTypes(field: GraphQLFieldDescriptor): Readonly<Record<string, string>> {
    const resolve = (argument: GraphQLArgumentDescriptor): string => {
      let name: string
      if ("builtin" in argument) name = argument.builtin
      else if ("scalar" in argument) {
        const scalar = this.scalars[argument.scalar]
        if (scalar === undefined)
          throw new Error(`Value object ${argument.scalar} has no scalar binding on this endpoint.`)
        name = scalar
      } else if ("enum" in argument) {
        name = argument.enum.name
        this.enums.set(name, argument.enum)
      } else if ("input" in argument) {
        const input = argument.input()
        name = input.name
        this.inputTypes.set(name, input)
      } else name = `[${resolve(argument.list)}]`
      return argument.nullable ? name : `${name}!`
    }
    const types = Object.fromEntries(
      Object.entries(field.args ?? {}).map(([name, argument]) => [name, resolve(argument)])
    )
    if (field.filter === undefined) return types
    // The `where` argument of a filterable field is always optional.
    const filter = field.filter()
    this.inputTypes.set(filter.name, filter)
    return { ...types, where: filter.name }
  }

  codec(type: GraphQLTypeDescriptor, selection: unknown): GraphQLQueryCodec {
    const convert = (value: unknown, text: string, encode: boolean): unknown => {
      if (value == null) return value
      const name = text.replace(/!$/, "")
      if (name.startsWith("["))
        return Array.isArray(value)
          ? value.map((v) => convert(v, name.slice(1, -1), encode))
          : convert(value, name.slice(1, -1), encode)
      const input = this.inputTypes.get(name)
      if (input !== undefined) return convertInput(value, input, encode)
      const enumeration = this.enums.get(name)
      return enumeration === undefined
        ? value
        : encode
          ? enumeration.toGraphQL(value as number)
          : enumeration.fromGraphQL(value as string)
    }
    const variables = (value: object, encode: boolean) =>
      Object.fromEntries(
        Object.entries(value).map(([key, v]) => [
          key,
          this.inputs.has(key) ? convert(v, this.inputs.get(key)!, encode) : v,
        ])
      )
    return {
      result: resultMapping(type, selection),
      encodeVariables: (value) => variables(value, true),
      decodeVariables: (value) => variables(value, false),
    }
  }
}

/** Whether a field takes arguments, so it is selected with args(...): declared ones, or a `where` filter. */
function takesArguments(field: GraphQLFieldDescriptor) {
  return field.args !== undefined || field.filter !== undefined
}

/** Converts the enum fields of an input object between application numbers and GraphQL names. */
function convertInput(value: unknown, shape: GraphQLInputShape, encode: boolean): unknown {
  if (!isObject(value) || shape.fields === undefined) return value
  const result: Record<string, unknown> = { ...value }
  for (const [key, field] of Object.entries(shape.fields)) {
    if (key in result) result[key] = convertInputField(result[key], field, encode)
  }
  return result
}

function convertInputField(value: unknown, field: GraphQLInputField, encode: boolean): unknown {
  if (value == null) return value
  if (Array.isArray(value)) return value.map((item) => convertInputField(item, field, encode))
  if ("enum" in field) return encode ? field.enum.toGraphQL(value as number) : field.enum.fromGraphQL(value as string)
  return convertInput(value, field.input(), encode)
}

function enumLiteral(value: unknown, path: string) {
  if (typeof value !== "string" || !identifier.test(value)) {
    throw new Error(`GraphQL argument ${path}: ${JSON.stringify(value)} is not an enum value.`)
  }
  return value
}

const parameters = new Proxy(
  {},
  { get: (_, name) => (typeof name === "string" ? { [variableMarker]: name } : undefined) }
)

function buildDocument(
  query: GraphQLTypeDescriptor,
  scalars: Readonly<Record<string, string>>,
  name: string,
  selection: unknown,
  connection: boolean
) {
  if (!identifier.test(name)) throw new Error(`"${name}" is not a valid GraphQL operation name.`)
  const builder = new DocumentBuilder(scalars)
  const body = builder.fields(query, selection, name, connection)
  const variables = [...builder.variables].map(([variable, type]) => `$${variable}: ${type}`).join(", ")
  return {
    document: `query ${name}${variables ? `(${variables})` : ""} { ${body} }` as AnyGraphQLDocument,
    codec: builder.codec(query, selection),
  }
}

/** Binds namespace-owned descriptors to one endpoint's scalar names. */
export function defineGraphQLEndpoint<TTypes extends GraphQLSchemaTypes>(
  endpoint: string,
  query: GraphQLTypeDescriptor,
  scalars: Readonly<Record<string, string>>
): GraphQLSchema<TTypes> {
  const schema = {
    endpoint,
    scalars,
    query: (name: string, build: (parameters: object) => unknown) =>
      defineGraphQLQuery({ endpoint, ...buildDocument(query, scalars, name, build(parameters), false) }),

    connectionQuery: (
      name: string,
      build: (parameters: object) => Selection,
      options: GraphQLConnectionQueryOptions<unknown, string, Readonly<Record<string, string>>, unknown> = {}
    ) => {
      const selection = build(parameters)
      const document = buildDocument(query, scalars, name, selection, true)
      const [key, ...others] = Object.keys(selection)
      // An alias of the paged field counts: its data arrives under the alias key.
      const aliased = key === undefined ? undefined : aliasContent(selection[key])
      if (
        key === undefined ||
        others.length > 0 ||
        markerContent(aliased === undefined ? selection[key] : aliased.selection, pagedMarker) === undefined
      ) {
        throw new Error(`GraphQL connection query ${name} must select exactly one paged field.`)
      }
      const sorted = query.fields[aliased?.field ?? key]?.sorted !== undefined
      return defineGraphQLConnectionQuery({
        ...options,
        endpoint,
        ...document,
        variables: (
          parametersOrPaging: object,
          paging?: ConnectionPagingVariablesFor<Readonly<Record<string, string>>>
        ) => {
          const parameters = paging == null ? {} : parametersOrPaging
          const { order, ...connection } = graphQLConnectionVariables(
            paging ?? (parametersOrPaging as ConnectionPagingVariablesFor<Readonly<Record<string, string>>>)
          )
          return { ...parameters, ...connection, ...(sorted ? { order } : {}) }
        },
        selectConnection: (data) => (data as Record<string, GraphQLConnection<unknown> | null>)[key] ?? { nodes: [] },
      })
    },

    select: (_name: string, selection: unknown) => selection,

    selection: (type: GraphQLTypeDescriptor, selection: unknown) =>
      createGraphQLSelection(
        new DocumentBuilder(scalars, false).fields(type, selection, type.name),
        resultMapping(type, selection)
      ),
  }
  return schema as unknown as GraphQLSchema<TTypes>
}

/** A feature's typed entry below Root's useCases field, independent of Root's descriptor graph. */
export interface GraphQLRootEntry<TType> {
  selection<const TSelection>(
    selection: TSelection & GraphQLObjectSelection<TType> & ValidSelection<TType, TSelection>
  ): GraphQLSelection<GraphQLObjectResult<TType, TSelection> | null>
  query<const TSelection>(
    name: string,
    selection: TSelection & GraphQLObjectSelection<TType> & ValidSelection<TType, TSelection>
  ): GraphQLQueryDefinition<GraphQLObjectResult<TType, TSelection> | null>
}

/** Defines a feature's Root selection using the existing framework selection format. */
export function defineRootEntry<TType>(
  path: readonly string[],
  type: GraphQLTypeDescriptor<TType>,
  root: Pick<GraphQLSchema<GraphQLSchemaTypes>, "endpoint" | "scalars">
): GraphQLRootEntry<TType> {
  if (path.length === 0 || path.some((segment) => !identifier.test(segment)))
    throw new Error("A Root entry needs a valid path.")
  const selection = (fields: unknown) => {
    const inner = resultMapping(type, fields)
    return createGraphQLSelection(
      wrapRootPath(path, new DocumentBuilder(root.scalars, false).fields(type, fields, type.name)),
      {
        decode: (value) => inner.decode(path.reduce<unknown>((v, key) => (isObject(v) ? v[key] : null), value) ?? null),
        encode: (value) => [...path].reverse().reduce<unknown>((v, key) => ({ [key]: v }), inner.encode(value)),
      }
    )
  }
  return {
    selection,
    query: (name: string, fields: unknown) => {
      const built = buildDocument(
        { name: "Query", fields: {} },
        root.scalars,
        name,
        { useCases: selection(fields) },
        false
      )
      const result = built.codec.result
      return defineGraphQLQuery({
        endpoint: root.endpoint,
        document: built.document,
        codec: {
          ...built.codec,
          result: {
            decode: (value) => (result.decode(value) as Selection).useCases,
            encode: (value) => result.encode({ useCases: value }),
          },
        },
      })
    },
  } as GraphQLRootEntry<TType>
}

function wrapRootPath(path: readonly string[], fields: string): string {
  return [...path].reverse().reduce((source, segment) => `${segment} { ${source} }`, fields)
}

/** Only selected enum/object fields need conversion; unrelated descriptors are never visited. */
function resultMapping(type: GraphQLTypeDescriptor, selection: unknown): GraphQLResultMapping {
  const fields: [string, GraphQLResultMapping][] = []
  for (const [key, entry] of Object.entries(selection as Selection)) {
    // The descriptor of the aliased field converts the data that arrives under the alias key.
    const aliased = aliasContent(entry)
    const field = type.fields[aliased?.field ?? key]
    const selected = aliased === undefined ? entry : aliased.selection
    const marked = markerContent(selected, argsMarker) ?? markerContent(selected, pagedMarker)
    const inner = marked === undefined ? selected : marked.selection
    let mapping = isGraphQLSelection(inner) ? inner.result : undefined
    if (mapping === undefined && field?.enum !== undefined) {
      const enumeration = field.enum
      mapping = {
        decode: (value) => enumeration.fromGraphQL(value as string),
        encode: (value) => enumeration.toGraphQL(value as number),
      }
    } else if (mapping === undefined && field?.type !== undefined && inner !== true)
      mapping = resultMapping(field.type(), inner)
    if (mapping === undefined) continue
    const nested = mapping
    const convert = (value: unknown, encode: boolean): unknown =>
      value == null
        ? value
        : Array.isArray(value)
          ? value.map((v) => convert(v, encode))
          : encode
            ? nested.encode(value)
            : nested.decode(value)
    fields.push([
      key,
      field?.paged
        ? {
            decode: (value) => (isObject(value) ? { ...value, nodes: convert(value.nodes, false) } : value),
            encode: (value) => (isObject(value) ? { ...value, nodes: convert(value.nodes, true) } : value),
          }
        : { decode: (value) => convert(value, false), encode: (value) => convert(value, true) },
    ])
  }
  const apply = (value: unknown, encode: boolean): unknown =>
    !isObject(value) || fields.length === 0
      ? value
      : {
          ...value,
          ...Object.fromEntries(
            fields
              .filter(([key]) => key in value)
              .map(([key, map]) => [key, encode ? map.encode(value[key]) : map.decode(value[key])])
          ),
        }
  return { decode: (value) => apply(value, false), encode: (value) => apply(value, true) }
}
